import React, {
	useEffect,
	useState,
	useRef,
	useCallback
} from 'react';
import PropTypes from 'prop-types';
import {
	makeStyles,
	RootRef,
	useTheme
} from '@material-ui/core';

import {
	VerticalTimeline as Timeline,
	VerticalTimelineElement
}  from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';

import MomentCore from 'moment';

import Constants from "../utils/Constants";
import {isJsonEqual} from "../utils/Json";
import PaperActivity from "./PaperActivity";

const lineColor = "#e9e9e9";
const styles = makeStyles(theme => ({
	timeline: {
		paddingTop: 0,
		paddingBottom: 0,
		marginBottom: theme.spacing(8)
	},

	openEndedLine: {
		"&:before": {
			backgroundImage: `linear-gradient(to top, ${lineColor}, 99%, ${theme.palette.common.white})`
		}
	},

	timelineElement: {
		marginTop: 0,
		width: "100%",
		position: "absolute",
		opacity: 0.7,

		"& > .vertical-timeline-element-content": {
			zoom: '25%',
			"-moz-transform": 'scale(1.3,.18)',
			"-moz-transform-origin": 'top ',
			width: '36%',
			boxShadow: 'none',
			background: 'transparent',

			"& p": {
				marginTop: "0.15em"
			}
		}
	},

	icon: {
		zIndex: 1,
		marginTop: 2,
		boxShadow: 'none'
	},

	eventPaper: {
		marginTop: theme.spacing(-3)
	},

	selectedEvent: {
		opacity: 1
	},

	selectedEventPaper: {
		border: `${theme.spacing(0.75)}px solid ${theme.palette.secondary.lightBlue}`, // theme.palette.primary.lime
		borderLeft: 0,
		borderRight: 0
	},

	/*date: {
		zoom: "100%",
		fontSize: "4em !important",
		color: "black"
	}*/

	hand: {
		cursor: "pointer"
	}
}));

const VerticalTimeline = React.memo(function VerticalTimeline(props) {
	const theme = useTheme();
	const lineRef = useRef();
	const [height, setHeight] = useState(0);
	const [adjustedEvents, setAdjustedEvents] = useState();

	let classes = styles(),
		{events, isOpenEnded, dateKey, reverseOrder, selectedEventIds, elementOnClick, detectionDate} = props;

	const adjust = useCallback(() => {
		if (events && events.length) {
			let evs = [...events]; // clone immutable events
			if (reverseOrder) {
				evs.reverse();
			}

			let unit = "days",
				start = new MomentCore(evs[0][dateKey]),
				end = new MomentCore(evs[evs.length-1][dateKey]),
				delta = start.diff(end, unit)+1; // +1 to include day

			if (Math.abs(delta) < 5) {
				unit = "hours";
				delta = start.diff(end, unit)+1; // +1 to include hour?
			}

			let lineHeight = lineRef && lineRef.current ? lineRef.current.offsetHeight : 0,
				pxPerUnit = (lineHeight / delta),
				adjusted = evs.map((event, idx) => {
					return {
						...event,
						...{
							top: !isOpenEnded && idx === (reverseOrder ? evs.length-1 : 0) ? 0 :
								Math.abs(((new MomentCore(event[dateKey])).diff(end, unit)+1) * pxPerUnit)
						}
					};
				});

			setAdjustedEvents(adjusted);
		}
	}, [events, setAdjustedEvents, isOpenEnded, dateKey, reverseOrder]);

	useEffect(() => {
		if (lineRef.current) {
			setHeight(lineRef.current.parentElement.offsetHeight);
		}
	}, [adjustedEvents]);

	useEffect(() => {
		adjust();
	}, [height, adjust]);

	return (
		<RootRef rootRef={lineRef}>
			<Timeline
				lineColor={lineColor}
				className={`${classes.timeline} ${isOpenEnded ? classes.openEndedLine : ""}`}>
				{adjustedEvents && adjustedEvents.length ? adjustedEvents.map((event, idx) => {
					let isSelected = selectedEventIds.includes(event.uuid),
						isDiscussion = event.category === Constants.ACTIVITY.CATEGORIES.DISCUSSIONS.ID,
						isDetection = Constants.PATTERN.isDetectionDate({detectedAt: detectionDate}, event.published);

					return (
						<VerticalTimelineElement
							key={idx}
							//date={event[dateKey]}
							//dateClassName={classes.date}
							onTimelineElementClick={() => elementOnClick(event.uuid)}
							className={`${classes.timelineElement} ${isSelected ? classes.selectedEvent : ""} ${elementOnClick ? classes.hand : ""}`}
							contentArrowStyle={{
								border: 'none',
								background: `${lineColor}`,
								height: theme.spacing(1),
								width: `${theme.spacing(4.25)}%`,
								marginTop: '0.2em'
							}}
							iconClassName={classes.icon}
							iconStyle={{
								marginLeft: -5,
								width: theme.spacing(1.25),
								height: theme.spacing(1.25),
								background: `${isDiscussion ?
									isDetection ? theme.palette.secondary.orange : theme.palette.primary.mediumGrey : theme.palette.error.main}`,
								zIndex: isDiscussion ? isDetection ? 2 : null : 1,
							}}
							style={{top: event.top}}>
							<PaperActivity
								showAvatarRow={false}
								numberOfViewableComments={0}
								elevation={isSelected ? 16 : undefined}
								className={`${classes.eventPaper} ${isSelected ? classes.selectedEventPaper : ""}`}
								{...{
									...event,
									...{
										language: null,
										topics: event.libraryTopics,
										time: event[dateKey],
										sourceIcon: Constants.SOURCES.getSourceIcon(event)
									}
								}} />
						</VerticalTimelineElement>
					);
				}) : <span></span>}
			</Timeline>
		</RootRef>
	);
}, (prevProps, newProps) => {
	// if they're the same, don't re-render (return true)
	return isJsonEqual(prevProps, newProps);
});

VerticalTimeline.displayName = "VerticalTimeline";

VerticalTimeline.defaultProps = {
	reverseOrder: false,
	isOpenEnded: false,
	dateKey: "publishedAt"
};

VerticalTimeline.propTypes = {
	reverseOrder: PropTypes.bool,
	events: PropTypes.array,
	isOpenEnded: PropTypes.bool,
	dateKey: PropTypes.string,
	selectedEventIds: PropTypes.array,
	elementOnClick: PropTypes.func,
	detectionDate: PropTypes.string
};

export default VerticalTimeline;